import { inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { EnabledSessionsStorageKeys } from 'app/shared/models/configs-sessions-storage';
import { AppState } from 'app/shared/state/models/app-state.model';
import { environment } from 'environments/environment';
import { Observable, of, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class OneTrustService {
  private renderer!: Renderer2;

  private readonly _rendererFactory = inject(RendererFactory2);
  private readonly _translateService = inject(TranslateService);

  private readonly _cookieConsentGiven: Subject<boolean> = new Subject<boolean>();

  public handleCookieConsent(): void {
    if (this.shouldShowCookiesModal()) {
      this.openCookiesModal();
    }
  }

  public cookieConsentGiven(): Observable<boolean> {
    if (!this.shouldShowCookiesModal()) {
      return of(true);
    }

    return this._cookieConsentGiven.asObservable();
  }

  private shouldShowCookiesModal(): boolean {
    return this.isOneTrustActive() && !this.hasStoredConsent();
  }

  private openCookiesModal(): void {
    this.renderer = this._rendererFactory.createRenderer(null, null);
    const headDom = document.getElementsByTagName('head')[0];
    const currentLanguage = this.getCurrentLanguage();

    const scriptGroupsAutoBlock = this.renderer.createElement('script') as HTMLScriptElement;
    scriptGroupsAutoBlock.type = 'text/javascript';
    scriptGroupsAutoBlock.src = environment.cookiePolicyConfig.autoBlockUri;
    this.renderer.appendChild(headDom, scriptGroupsAutoBlock);

    const scriptGroupsSdk = this.renderer.createElement('script') as HTMLScriptElement;
    scriptGroupsSdk.type = 'text/javascript';
    scriptGroupsSdk.src = environment.cookiePolicyConfig.sdkUri;
    scriptGroupsSdk.integrity = environment.cookiePolicyConfig.hashIntegritySdk;
    scriptGroupsSdk.crossOrigin = 'anonymous';
    scriptGroupsSdk.setAttribute('charset', 'UTF-8');
    scriptGroupsSdk.setAttribute('data-domain-script', environment.cookiePolicyConfig?.dataDomainScript);
    scriptGroupsSdk.setAttribute('data-document-language', 'true');
    scriptGroupsSdk.setAttribute('data-language', currentLanguage);
    this.renderer.appendChild(headDom, scriptGroupsSdk);

    const scriptGroupsWrapper = this.renderer.createElement('script') as HTMLScriptElement;
    scriptGroupsWrapper.type = 'text/javascript';
    scriptGroupsWrapper.text = 'function OptanonWrapper() { }';
    this.renderer.appendChild(headDom, scriptGroupsWrapper);

    window.addEventListener('OTConsentApplied', () => {
      this._cookieConsentGiven.next(true);
    });
  }

  private isOneTrustActive(): boolean {
    return environment.cookiePolicyConfig.active;
  }

  private hasStoredConsent(): boolean {
    const optanonConsent = localStorage.getItem('OptanonConsent');
    const cookieConsent = document.cookie.includes('OptanonConsent');
    const optanonAlertBoxClosed = localStorage.getItem('OptanonAlertBoxClosed');
    const alertBoxClosed = document.cookie.includes('OptanonAlertBoxClosed');

    return !!((optanonConsent || cookieConsent) && (optanonAlertBoxClosed || alertBoxClosed));
  }

  private getCurrentLanguage(): string {
    const currentSession = JSON.parse(sessionStorage.getItem(EnabledSessionsStorageKeys.state) ?? '{}') as AppState;
    if (currentSession?.language) {
      return currentSession.language.split('-')[0];
    }
    return (
      this._translateService.currentLang?.split('-')[0] ??
      environment.internationalization.defaultLanguage.split('-')[0]
    );
  }
}
